/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CertificatesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the certificates module.
----------------------------------------------------------------*/


import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Plus, Edit, Trash2, CheckCircle, AlertTriangle, Clock, XCircle, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { fetchCertificates, deleteCertificate } from './api';
import { CertificateListItem, CertificateStatus, CertificateType } from './types';

// Column type matching DataGridSimple expectations
interface CertificateColumn {
    key: string;
    label: string;
    sortable?: boolean;
    visible: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (value: any, item: any) => React.ReactNode;
}

export default function CertificatesPage() {
    const navigate = useNavigate();
    const { t, i18n } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; certificate: CertificateListItem | null }>({
        isOpen: false,
        certificate: null,
    });

    // Data state
    const [data, setData] = useState<CertificateListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    });

    // Helper functions
    const getStatusBadge = useCallback((status: CertificateStatus) => {
        const statusConfig: Record<CertificateStatus, { className: string; icon: React.ReactNode }> = {
            ACTIVE: { className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: <CheckCircle className="w-3 h-3" /> },
            PENDING: { className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: <Clock className="w-3 h-3" /> },
            EXPIRED: { className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: <AlertTriangle className="w-3 h-3" /> },
            REVOKED: { className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: <XCircle className="w-3 h-3" /> },
            ERROR: { className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: <AlertTriangle className="w-3 h-3" /> },
        };

        const config = statusConfig[status];
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
                {config.icon}
                {t(`certificates.status.${status.toLowerCase()}`)}
            </span>
        );
    }, [t]);

    const getTypeBadge = useCallback((type: CertificateType) => {
        const typeConfig: Record<CertificateType, string> = {
            SELF_SIGNED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
            LETS_ENCRYPT: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            CUSTOM: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
        };

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${typeConfig[type]}`}>
                {t(`certificates.type.${type.toLowerCase()}`)}
            </span>
        );
    }, [t]);

    const formatDate = (dateStr: string | null | undefined): string => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString();
    };

    const getDaysUntilExpiry = (notAfter: string | null | undefined): React.ReactNode => {
        if (!notAfter) return null;
        const days = Math.ceil((new Date(notAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

        if (days < 0) {
            return <span className="text-red-500 text-xs">({t('certificates.expired')})</span>;
        } else if (days <= 30) {
            return <span className="text-yellow-500 text-xs">({days} {t('certificates.daysLeft')})</span>;
        }
        return <span className="text-muted-foreground text-xs">({days} {t('certificates.daysLeft')})</span>;
    };

    // Define columns
    const columns = useMemo<CertificateColumn[]>(() => [
        {
            key: 'name',
            label: t('certificates.columns.name'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: CertificateListItem) => (
                <div className="flex flex-col">
                    <span className="font-medium text-primary">{item.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{item.commonName}</span>
                </div>
            ),
        },
        {
            key: 'type',
            label: t('certificates.columns.type'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: CertificateListItem) => getTypeBadge(item.type),
        },
        {
            key: 'status',
            label: t('certificates.columns.status'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: CertificateListItem) => {
                // Calculate if certificate is expiring (within renew period)
                const daysLeft = item.notAfter
                    ? Math.ceil((new Date(item.notAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                    : null;
                const isExpiring = daysLeft !== null && daysLeft > 0 && daysLeft <= 30;

                return (
                    <div className="flex flex-col gap-1">
                        {getStatusBadge(item.status)}
                        {isExpiring && item.status === 'ACTIVE' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                <AlertTriangle className="w-3 h-3" />
                                {t('certificates.expiring')}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'issuer',
            label: t('certificates.columns.issuer'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: CertificateListItem) => (
                <span className="text-sm text-muted-foreground truncate max-w-[150px] block" title={item.issuer || '-'}>
                    {item.issuer || '-'}
                </span>
            ),
        },
        {
            key: 'notAfter',
            label: t('certificates.columns.expiresOn'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: CertificateListItem) => (
                <div className="flex flex-col">
                    <span>{formatDate(item.notAfter)}</span>
                    {getDaysUntilExpiry(item.notAfter)}
                </div>
            ),
        },
        {
            key: 'actions',
            label: t('common.actions'),
            sortable: false,
            visible: true,
        },
    ], [t, i18n.language, getStatusBadge, getTypeBadge]);

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<CertificateColumn[]>(columns);

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchCertificates(searchQuery || undefined);
            setData(result);
            setPagination(prev => ({
                ...prev,
                total: result.length,
                totalPages: Math.ceil(result.length / prev.limit),
            }));
        } catch (error) {
            console.error('Failed to load certificates:', error);
            showError(t('certificates.messages.loadError'));
        } finally {
            setLoading(false);
        }
    }, [searchQuery, showError, t]);

    // Initial load
    useEffect(() => {
        loadData();
    }, []);

    // Handlers
    const handleCreate = useCallback(() => {
        navigate('/admin/system-settings/certificates/new');
    }, [navigate]);

    const handleEdit = useCallback((cert: CertificateListItem) => {
        navigate(`/admin/system-settings/certificates/${cert.uuid}`);
    }, [navigate]);

    const handleDelete = useCallback((cert: CertificateListItem) => {
        setDeleteModal({ isOpen: true, certificate: cert });
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteModal.certificate) return;

        try {
            await deleteCertificate(deleteModal.certificate.id);
            showSuccess(t('certificates.messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, certificate: null });
            loadData();
        } catch (error) {
            console.error('Error deleting certificate:', error);
            showError(error.message || t('certificates.messages.deleteError'));
        }
    }, [deleteModal.certificate, loadData, showError, showSuccess, t]);



    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const handleSort = useCallback((column: string) => {
        if (sortBy === column) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    }, [sortBy]);

    const handlePageChange = useCallback((page: number) => {
        setPagination(prev => ({ ...prev, page }));
    }, []);

    const handleItemsPerPageChange = useCallback((limit: number) => {
        setPagination(prev => ({ ...prev, limit, page: 1 }));
    }, []);

    const handleColumnToggle = useCallback((key: string) => {
        setVisibleColumns(prev =>
            prev.map(col => col.key === key ? { ...col, visible: !col.visible } : col)
        );
    }, []);

    // Render actions for each row
    const renderActions = useCallback((cert: CertificateListItem) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.edit')} position="top">
                <button
                    onClick={() => handleEdit(cert)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete')} position="top">
                <button
                    onClick={() => handleDelete(cert)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, handleEdit, handleDelete]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={data}
                loading={loading}
                columns={visibleColumns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                renderActions={renderActions}
                searchPlaceholder={t('certificates.searchPlaceholder')}
                title={t('certificates.title')}
                titleIcon={<Shield className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('certificates.actions.add')} position="top">
                            <button
                                onClick={handleCreate}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('certificates.actions.add')}
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, certificate: null })}
                onConfirm={confirmDelete}
                title={t('certificates.deleteModal.title')}
                message={`${t('certificates.deleteModal.message')} (${deleteModal.certificate?.name})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
            />
        </div>
    );
}
